import { useEffect, useRef, type MutableRefObject } from "react";

// Smooth mic level (0–1) from a MediaStream via AnalyserNode.
export const useMicLevel = (
  stream: MediaStream | null,
  enabled: boolean,
): { levelRef: MutableRefObject<number> } => {
  const levelRef = useRef(0);
  const smoothedRef = useRef(0);

  useEffect(() => {
    if (!enabled || !stream) {
      smoothedRef.current = 0;
      levelRef.current = 0;
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;

    const start = async () => {
      try {
        const Ctx =
          window.AudioContext ||
          (
            window as unknown as {
              webkitAudioContext: typeof AudioContext;
            }
          ).webkitAudioContext;
        audioContext = new Ctx();
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }
        if (cancelled) return;

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.72;
        source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const data = new Uint8Array(analyser.fftSize);

        const tick = () => {
          if (cancelled || !analyser) return;

          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i += 1) {
            const n = (data[i] - 128) / 128;
            sum += n * n;
          }
          const rms = Math.sqrt(sum / data.length);

          const floored = Math.max(0, rms - 0.018);
          const raw = Math.min(1, floored * 8.5);
          const shaped = Math.pow(raw, 0.85);

          const prev = smoothedRef.current;
          const attack = 0.55;
          const release = shaped > 0.5 ? 0.12 : 0.2;
          const alpha = shaped > prev ? attack : release;
          const next = prev + (shaped - prev) * alpha;
          smoothedRef.current = next;
          levelRef.current = next;

          rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
      } catch (error) {
        console.error("Failed to analyse mic level:", error);
        levelRef.current = 0;
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      try {
        source?.disconnect();
      } catch {
        // ignore
      }
      try {
        analyser?.disconnect();
      } catch {
        // ignore
      }
      void audioContext?.close();
      smoothedRef.current = 0;
      levelRef.current = 0;
    };
  }, [stream, enabled]);

  return { levelRef };
};
